import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PreferencesSetup from './PreferencesSetup';

interface PreferencesGuardProps {
  children: React.ReactNode;
}

const PreferencesGuard: React.FC<PreferencesGuardProps> = ({ children }) => {
  const { isAuthenticated, hasPreferences, user } = useAuth();
  const [showPreferences, setShowPreferences] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkPreferences = async () => {
      if (isAuthenticated && user) {
        // Check if user has preferences
        const hasPrefs = hasPreferences();
        
        // If user is authenticated but doesn't have preferences, show setup
        // But only if they haven't already skipped it for this specific user
        const hasSkipped = sessionStorage.getItem(`preferences_skipped_${user.id}`);
        
        if (!hasPrefs && !hasSkipped) {
          setShowPreferences(true);
        }
      }
      setIsChecking(false);
    };

    checkPreferences();
  }, [isAuthenticated, user, hasPreferences]);

  const handlePreferencesComplete = () => {
    setShowPreferences(false);
  };

  const handlePreferencesSkip = () => {
    // Mark as skipped in session storage for this specific user
    if (user) {
      sessionStorage.setItem(`preferences_skipped_${user.id}`, 'true');
    }
    setShowPreferences(false);
  };

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Show preferences setup if needed
  if (showPreferences) {
    return <PreferencesSetup onComplete={handlePreferencesComplete} onSkip={handlePreferencesSkip} />;
  }

  // Show normal app content
  return <>{children}</>;
};

export default PreferencesGuard;
